/**
 * Auslegung der Stripe-Abo-Status — eine Quelle für Frontend und Server.
 *
 * Vorher lagen drei Auslegungen nebeneinander: Das Frontend zählte `past_due` als aktiv
 * und bot den Buchungs-Wizard an, die Server-Middleware ließ nur `active`/`trialing` durch
 * und antwortete sonst 403, und die Checkout-Sperre griff ebenfalls nur bei diesen beiden.
 * Ergebnis: ein überfälliger Kunde sah einen Wizard, der ihm 403 lieferte, und konnte
 * gleichzeitig ein zweites Abo kaufen.
 *
 * Die drei Fragen sind bewusst getrennt, weil sie verschiedene Antworten haben:
 *   - Darf gebucht werden?            → canBookSessions        (muss zum Server passen)
 *   - Existiert ein zeigbares Abo?    → isSubscriptionVisible  (Kachel statt Werbung)
 *   - Ist ein Neukauf zu sperren?     → hasRepairableSubscription
 *
 * ACHTUNG: Diese Datei wird auch von der Express-Schicht unter `src/server/` importiert (wie
 * `account-name-rules`). Sie muss deshalb frei von Angular-Imports bleiben — sonst zieht das
 * Server-Bundle das Framework mit.
 */

/** Leistung freigeschaltet. Muss deckungsgleich mit `requireActiveSubscription` bleiben. */
const BOOKABLE = new Set(['active', 'trialing']);

/**
 * Abo existiert und geht den Kunden etwas an — auch wenn die Zahlung klemmt. `canceled` fehlt
 * bewusst: Die Zeile bleibt nach dem Periodenende bestehen, der Kunde ist aber wieder
 * Interessent und soll das Angebot sehen.
 *
 * `unpaid` und `paused` fehlen hier ebenfalls, obwohl sie fachlich hineingehörten. Sie
 * aufzunehmen hieße, sie überall nachzuziehen: die Abo-Kachel rendert für sie sonst den
 * Werbe-Leerzustand ohne Portal-Button, und `statusLabel` hat keine Übersetzung für sie
 * (der rohe englische Stripe-String stünde in der UI). Bis dahin bleibt es beim bisherigen
 * Verhalten, statt eine Sackgasse zu bauen. Vermerkt in docs/abo-prod-readiness.md.
 */
const VISIBLE = new Set(['active', 'trialing', 'past_due']);

/**
 * Bei Stripe liegt ein Abo, das der Kunde im Billing-Portal in Ordnung bringen kann.
 * Ein zweiter Checkout erzeugte hier ein zweites, parallel laufendes Abo.
 * `incomplete` fehlt bewusst: Dort kam die erste Zahlung nie durch, Stripe räumt die
 * Zeile selbst ab, und ein sauberer Neuversuch ist die richtige Antwort.
 */
const REPAIRABLE = new Set(['active', 'trialing', 'past_due', 'unpaid', 'paused']);

type Status = string | null | undefined;

/** Darf dieser Status Sessions buchen? Deckt sich mit der Server-Middleware. */
export function canBookSessions(status: Status): boolean {
    return BOOKABLE.has(status ?? '');
}

/** Soll die Abo-Kachel gezeigt werden statt des Werbe-Leerzustands? */
export function isSubscriptionVisible(status: Status): boolean {
    return VISIBLE.has(status ?? '');
}

/** Soll ein neuer Checkout gesperrt und stattdessen das Portal geöffnet werden? */
export function hasRepairableSubscription(status: Status): boolean {
    return REPAIRABLE.has(status ?? '');
}
